import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import type {
  YouTubeChannel,
  CuratedChannel,
  WatchHistoryEntry,
  YouTubeVideo,
  AccountSummary,
  InviteCode,
  KidProfile,
  PlaybackSettings,
} from '@kidstube/types';
import { apiFetch, apiUrl } from '@kidstube/lib/api';
import { decodeHtmlEntities } from '@kidstube/lib/text';

const buildInviteLink = (code: string) =>
  apiUrl(`/api/auth/login?invite=${encodeURIComponent(code)}&returnTo=${encodeURIComponent(window.location.origin)}`);

export default function ParentDashboard() {
  const location = useLocation();

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        <h3 style={{ marginBottom: 'var(--spacing-lg)' }}>⚙️ Parent Controls</h3>
        <nav className="dashboard-nav">
          <Link
            to="/parent"
            className={`dashboard-nav-item ${location.pathname === '/parent' ? 'active' : ''}`}
          >
            📺 Channels
          </Link>
          <Link
            to="/parent/history"
            className={`dashboard-nav-item ${location.pathname === '/parent/history' ? 'active' : ''}`}
          >
            📊 Watch History
          </Link>
          <Link
            to="/parent/family"
            className={`dashboard-nav-item ${location.pathname === '/parent/family' ? 'active' : ''}`}
          >
            👨‍👩‍👧 Family
          </Link>
          <Link
            to="/parent/playback"
            className={`dashboard-nav-item ${location.pathname === '/parent/playback' ? 'active' : ''}`}
          >
            Playback
          </Link>
        </nav>
      </aside>

      <div className="dashboard-content">
        <Routes>
          <Route path="/" element={<ChannelManagement />} />
          <Route path="/history" element={<WatchHistory />} />
          <Route path="/family" element={<FamilyManagement />} />
          <Route path="/playback" element={<PlaybackManagement />} />
        </Routes>
      </div>
    </div>
  );
}

function PlaybackManagement() {
  const [settings, setSettings] = useState<PlaybackSettings>({ preferDirectPlayback: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ settings?: PlaybackSettings }>('/api/playback-settings')
      .then((data) => setSettings(data.settings || { preferDirectPlayback: false }))
      .catch(() => setMessage('Could not load playback settings.'))
      .finally(() => setLoading(false));
  }, []);

  async function toggleDirectPlayback() {
    const next = !settings.preferDirectPlayback;
    setSaving(true);
    setMessage(null);
    try {
      const result = await apiFetch<{ settings: PlaybackSettings }>('/api/playback-settings', {
        method: 'POST',
        json: { preferDirectPlayback: next },
      });
      setSettings(result.settings);
      setMessage(next
        ? 'Direct 360p playback is now preferred on every client.'
        : 'YouTube embeds are now preferred, with direct 360p as the fallback.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not save playback settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Playback</h2>
        <p className="page-description">Choose the default player used by the web app and TV apps.</p>
      </div>

      <div className="settings-grid">
        <section className="settings-card">
          <div className="settings-card-header">
            <div>
              <div className="settings-card-title">Always prefer direct 360p</div>
              <div className="settings-card-subtitle">
                Skip the YouTube iframe and request the muxed MP4 itag 18 stream directly.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={settings.preferDirectPlayback}
              aria-label="Always prefer direct 360p playback"
              className={`toggle-switch ${settings.preferDirectPlayback ? 'is-on' : ''}`}
              onClick={toggleDirectPlayback}
              disabled={loading || saving}
            >
              <span className="toggle-switch-knob" />
            </button>
          </div>
          <p className="settings-card-subtitle">
            {settings.preferDirectPlayback
              ? 'Direct mode: itag 18 → unable to play. The iframe is skipped.'
              : 'Standard mode: YouTube iframe → itag 18 fallback → unable to play.'}
          </p>
          {message && <p className="settings-save-message">{message}</p>}
        </section>
      </div>
    </div>
  );
}

function ChannelManagement() {
  const [channels, setChannels] = useState<(YouTubeChannel & CuratedChannel)[]>([]);
  const [loading, setLoading] = useState(true);
  const [newChannelUrl, setNewChannelUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchChannels();
  }, []);

  async function fetchChannels() {
    try {
      const data = await apiFetch<{ channels?: (YouTubeChannel & CuratedChannel)[] }>('/api/channels');
      setChannels(data.channels || []);
    } catch (err) {
      console.error('Failed to fetch channels:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddChannel(e: React.FormEvent) {
    e.preventDefault();
    if (!newChannelUrl.trim()) return;

    setAdding(true);
    setMessage(null);

    try {
      const data = await apiFetch<{ channel?: { title: string } }>('/api/channels', {
        method: 'POST',
        json: { channelUrl: newChannelUrl },
      });
      setMessage({ type: 'success', text: `Added "${decodeHtmlEntities(data.channel?.title)}" successfully!` });
      setNewChannelUrl('');
      fetchChannels();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to add channel. Please try again.' });
    } finally {
      setAdding(false);
    }
  }

  async function handleRemoveChannel(channelId: string) {
    if (!confirm('Are you sure you want to remove this channel?')) return;

    try {
      await apiFetch(`/api/channels/${channelId}`, { method: 'DELETE' });
      setChannels(channels.filter((c) => c.channelId !== channelId));
      setMessage({ type: 'success', text: 'Channel removed successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to remove channel' });
    }
  }

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">📺 Manage Channels</h2>
        <p className="page-description">
          Add YouTube channels that your child can watch. Only videos from these channels will appear in their feed.
        </p>
      </div>

      <form onSubmit={handleAddChannel} style={{ marginBottom: 'var(--spacing-xl)' }}>
        <div className="form-group">
          <label className="form-label">Add a Channel</label>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Paste YouTube channel URL or @handle..."
              value={newChannelUrl}
              onChange={(e) => setNewChannelUrl(e.target.value)}
              disabled={adding}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={adding || !newChannelUrl.trim()}
            >
              {adding ? 'Adding...' : '+ Add'}
            </button>
          </div>
          <p style={{ marginTop: 'var(--spacing-sm)', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            Examples: https://www.youtube.com/@ChannelName or https://www.youtube.com/channel/UC...
          </p>
        </div>
      </form>

      {message && (
        <div
          style={{
            padding: 'var(--spacing-md)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--spacing-lg)',
            background: message.type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)',
            color: 'white',
          }}
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="loading-text">Loading channels...</div>
      ) : channels.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📺</div>
          <h3 className="empty-state-title">No channels yet</h3>
          <p>Add your first channel above to get started!</p>
        </div>
      ) : (
        <div className="channel-list">
          {channels.map((channel) => (
            <div key={channel.channelId} className="channel-list-item">
              <img src={channel.thumbnail} alt={decodeHtmlEntities(channel.title)} />
              <div className="channel-list-info">
                <div className="channel-list-title">{decodeHtmlEntities(channel.title)}</div>
              </div>
              <div className="channel-list-actions">
                <a
                  href={`https://youtube.com/channel/${channel.channelId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  View
                </a>
                <button
                  className="btn btn-danger"
                  onClick={() => handleRemoveChannel(channel.channelId)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FamilyManagement() {
  const [account, setAccount] = useState<AccountSummary | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [inviteRole, setInviteRole] = useState<'parent' | 'child'>('child');
  const [inviteName, setInviteName] = useState('');
  const [inviteAvatar, setInviteAvatar] = useState('');
  const [inviteUses, setInviteUses] = useState(1);
  const [inviteDays, setInviteDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [selectedMember, setSelectedMember] = useState<{
    id: string;
    name: string;
    role: 'parent' | 'child';
    email?: string;
  } | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  async function fetchSettings() {
    try {
      const data = await apiFetch<AccountSummary>('/api/settings');
      setAccount(data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }

  useEffect(() => {
    fetchSettings();
    fetchCurrentUser();
  }, []);

  async function fetchCurrentUser() {
    try {
      const data = await apiFetch<{ userId?: string }>('/api/auth/me');
      setCurrentUserId(data.userId || null);
    } catch {
      setCurrentUserId(null);
    }
  }

  async function handleCreateInvite(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await apiFetch('/api/invites', {
        method: 'POST',
        json: {
          role: inviteRole,
          usesRemaining: inviteUses,
          expiresInDays: inviteDays,
          childName: inviteRole === 'child' ? inviteName : undefined,
          childAvatar: inviteRole === 'child' ? inviteAvatar : undefined,
        },
      });
      setInviteName('');
      setInviteAvatar('');
      setShowInviteForm(false);
      await fetchSettings();
      setMessage({ type: 'success', text: 'Invite created.' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteInvite(code: string) {
    await apiFetch(`/api/invites/${code}`, { method: 'DELETE' });
    await fetchSettings();
  }

  async function handleRemoveMember() {
    if (!selectedMember) return;
    try {
      await apiFetch(`/api/family/${selectedMember.id}`, { method: 'DELETE' });
      setMessage({ type: 'success', text: `${selectedMember.name} removed.` });
      setSelectedMember(null);
      await fetchSettings();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Could not remove member.' });
    }
  }

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">👨‍👩‍👧 Family</h2>
        <p className="page-description">Invite parents and kids to your account.</p>
      </div>

      {message && (
        <div
          style={{
            padding: 'var(--spacing-md)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--spacing-lg)',
            background: message.type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)',
            color: 'white',
          }}
        >
          {message.text}
        </div>
      )}

      <div style={{ display: 'grid', gap: 'var(--spacing-lg)' }}>
        <section>
          <h3>Family Members</h3>
          <div className="family-grid">
            {(account?.parents || []).map((parent) => (
              <button
                key={parent.id}
                className="family-tile"
                type="button"
                onClick={() => setSelectedMember({
                  id: parent.id,
                  name: parent.name || parent.email || 'Parent',
                  role: 'parent',
                  email: parent.email,
                })}
              >
                <div className="family-tile-avatar">
                  {parent.picture ? (
                    <img src={parent.picture} alt={parent.name || parent.email} />
                  ) : (
                    parent.name?.[0] || parent.email[0].toUpperCase()
                  )}
                </div>
                <div className="family-tile-name">{parent.name || parent.email}</div>
                <div className="family-tile-role">Parent</div>
              </button>
            ))}
            {(account?.members || [])
              .filter((member) => member.role === 'child' && member.childId)
              .map((member) => {
                const profile = account?.children?.find((child) => child.id === member.childId);
                const displayName = profile?.name || member.name || member.email || 'Child';
                return (
                  <button
                    key={member.id}
                    className="family-tile"
                    type="button"
                    onClick={() => setSelectedMember({
                      id: member.id,
                      name: displayName,
                      role: 'child',
                      email: member.email,
                    })}
                  >
                    <div className="family-tile-avatar">
                      {member.picture ? (
                        <img src={member.picture} alt={displayName} />
                      ) : (
                        profile?.avatar || displayName[0]?.toUpperCase() || '🧒'
                      )}
                    </div>
                    <div className="family-tile-name">{displayName}</div>
                    <div className="family-tile-role">Child</div>
                  </button>
                );
              })}
            <button
              className="family-tile family-tile-add"
              type="button"
              onClick={() => setShowInviteForm(true)}
            >
              <div className="family-tile-avatar">+</div>
              <div className="family-tile-name">Add Family</div>
              <div className="family-tile-role">Invite</div>
            </button>
          </div>
        </section>

        {selectedMember && (
          <section>
            <h3>{selectedMember.name}</h3>
            <div style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacing-md)' }}>
              {selectedMember.role === 'parent' ? 'Parent account' : 'Child account'}
              {selectedMember.email ? ` • ${selectedMember.email}` : ''}
            </div>
            <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
              <button
                className="btn btn-danger"
                type="button"
                disabled={selectedMember.id === currentUserId}
                onClick={handleRemoveMember}
              >
                Remove access
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setSelectedMember(null)}
              >
                Close
              </button>
            </div>
            {selectedMember.id === currentUserId && (
              <p style={{ marginTop: 'var(--spacing-sm)', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                You cannot remove yourself.
              </p>
            )}
          </section>
        )}

        {showInviteForm && (
          <section>
            <h3>Invite a family member</h3>
            <form onSubmit={handleCreateInvite} style={{ marginTop: 'var(--spacing-md)' }}>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  className="form-input"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'parent' | 'child')}
                >
                  <option value="child">Child</option>
                  <option value="parent">Parent</option>
                </select>
              </div>
              {inviteRole === 'child' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Child name</label>
                    <input
                      className="form-input"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      placeholder="e.g. Alex"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Child avatar</label>
                    <input
                      className="form-input"
                      value={inviteAvatar}
                      onChange={(e) => setInviteAvatar(e.target.value)}
                      placeholder="e.g. 🧒"
                    />
                  </div>
                </>
              )}
              <div className="form-group">
                <label className="form-label">Uses</label>
                <input
                  type="number"
                  className="form-input"
                  value={inviteUses}
                  onChange={(e) => setInviteUses(Number(e.target.value))}
                  min={1}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Expires in (days)</label>
                <input
                  type="number"
                  className="form-input"
                  value={inviteDays}
                  onChange={(e) => setInviteDays(Number(e.target.value))}
                  min={1}
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Invite'}
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setShowInviteForm(false)}
                >
                  Done
                </button>
              </div>
            </form>
          </section>
        )}

        <section>
          <h3>Active Invites</h3>
          <ul>
            {(account?.invites || []).map((invite: InviteCode) => (
              <li key={invite.code} style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'center' }}>
                <span>{invite.code} ({invite.role})</span>
                <a className="btn btn-secondary" href={buildInviteLink(invite.code)}>
                  Invite Link
                </a>
                <button className="btn btn-danger" onClick={() => handleDeleteInvite(invite.code)}>
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function WatchHistory() {
  const [history, setHistory] = useState<(WatchHistoryEntry & { video?: YouTubeVideo })[]>([]);
  const [children, setChildren] = useState<KidProfile[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchChildren() {
      try {
        const data = await apiFetch<AccountSummary>('/api/settings');
        setChildren(data.children || []);
        if (data.children?.length) {
          setSelectedChildId((current) => current || data.children[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch children:', err);
      }
    }
    fetchChildren();
  }, []);

  useEffect(() => {
    async function fetchHistory() {
      if (!selectedChildId) return;
      setLoading(true);
      try {
        const data = await apiFetch<{ history?: (WatchHistoryEntry & { video?: YouTubeVideo })[] }>(
          `/api/watch-history?childId=${selectedChildId}`
        );
        setHistory(data.history || []);
      } catch (err) {
        console.error('Failed to fetch history:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchHistory();
  }, [selectedChildId]);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">📊 Watch History</h2>
        <p className="page-description">See what videos have been watched recently.</p>
      </div>

      <div className="form-group" style={{ maxWidth: '320px' }}>
        <label className="form-label">Child profile</label>
        <select
          className="form-input"
          value={selectedChildId}
          onChange={(e) => setSelectedChildId(e.target.value)}
        >
          {children.map((child) => (
            <option key={child.id} value={child.id}>
              {child.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading-text">Loading history...</div>
      ) : history.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📺</div>
          <h3 className="empty-state-title">No watch history yet</h3>
          <p>Videos watched will appear here.</p>
        </div>
      ) : (
        <div className="channel-list">
          {history.map((entry, index) => (
            <div key={`${entry.videoId}-${index}`} className="channel-list-item">
              {entry.video ? (
                <>
                  <img
                    src={entry.video.thumbnail}
                    alt={decodeHtmlEntities(entry.video.title)}
                    style={{ width: '120px', height: '68px', borderRadius: 'var(--radius-sm)' }}
                  />
                  <div className="channel-list-info">
                    <div className="channel-list-title">{decodeHtmlEntities(entry.video.title)}</div>
                    <div className="channel-list-meta">
                      {decodeHtmlEntities(entry.video.channelTitle)} • Watched{' '}
                      {new Date(entry.lastWatchedAt).toLocaleDateString()}
                    </div>
                  </div>
                </>
              ) : (
                <div className="channel-list-info">
                  <div className="channel-list-title">Video ID: {entry.videoId}</div>
                  <div className="channel-list-meta">
                    Watched {new Date(entry.lastWatchedAt).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
